import { Injector } from "@angular/core";
import moment from 'moment';
import { Observable, Subject } from "rxjs";
import { INamedClass } from "../../common/iNamedClass";
import { LoggerService } from "../../common/logging/logger.service";
import { MockProviderService } from "../../util/mockProvider.service";
import { KpiServiceError, KpiServiceErrorType } from "./kpiServiceError";

export class UpdatingEventPayLoad {
    public source: string;
    public requestId: number;

    constructor(source: string, requestId: number) {
        this.source = source;
        this.requestId = requestId;
    }
}

export class UpdateOkEventPayload<T> {
    public source: string;
    public requestId: number;
    public data: T;

    constructor(source: string, requestId: number, data: T) {
        this.source = source;
        this.requestId = requestId;
        this.data = data;
    }
}

export class UpdateKoEventPayload {
    public source: string;
    public requestId: number;
    public error: KpiServiceError;

    constructor(source: string, requestId: number, error: KpiServiceError) {
        this.source = source;
        this.requestId = requestId;
        this.error = error;
    }
}

export abstract class KpiService<T> implements INamedClass {
    public abstract getClassName(): string;

    private readonly cacheExpiration: number = 30000;

    private updating: boolean = false;
    private requestId: number = 0;

    private useMockProvider: boolean = false;
    private lastUpdateSuccessful : boolean = false;
    private lastData: T = null;
    private lastDataTimestamp: Date = null;
    private lastError: any = null;
    protected logger: LoggerService;
    private mockProvider: MockProviderService;

    constructor(private injector: Injector) {
        this.logger = injector.get(LoggerService);
        try {
            if (this.useMockProvider) {
                this.mockProvider = injector.get(MockProviderService);
            }
        } catch {
            this.mockProvider = null;
        }
    }

    public getLastData(): T {
        if (this.lastError) throw this.lastError;
        else return this.lastData;
    }

    public get isUpdating(): boolean {
        return this.updating;
    }

    protected abstract update(...args: any[]): Promise<T>;

    public getRequestId(): number {
        if (this.updating) return this.requestId;
        else return ++this.requestId;
    }

    public refresh(requestId: number, ...args: any[]): void {
        if (!this.updating) {
            this.updating = true;
            this.updatingSource.next(new UpdatingEventPayLoad(this.getClassName(), requestId));

            if (this.cacheExpired()) {
                this.performRequest(requestId);
            } else {
                this.useCachedResponse(requestId);
            }
        } else {
            this.updatingSource.next(new UpdatingEventPayLoad(this.getClassName(), requestId));
        }
    }

    private async performRequest(currentRequestId : number) : Promise<void> {
        try {
            let data: T = null;
            if (this.mockProvider) {
                try {
                    let callingClass = this.getClassName();
                    data = await this.mockProvider.getResponse<T>(this.getClassName());
                } catch {
                    let start = moment();
                    try {
                        data = await this.update();
                    } finally {
                        let end = moment();
                        this.logger.debug("KpiTiming-{0}".format(this.getClassName()), "{0}".format(end.diff(start, 'millisecond').toString()));
                    }
                }
            }
            else {
                let start = moment();
                try {
                    data = await this.update();
                } finally {
                    let end = moment();
                    this.logger.debug("KpiTiming-{0}".format(this.getClassName()), "{0}".format(end.diff(start, 'millisecond').toString()));
                }
            }
            this.lastUpdateSuccessful = true;
            this.lastData = data;
            this.lastDataTimestamp = new Date();
            this.lastError = null;
            this.updateOkSource.next(new UpdateOkEventPayload(this.getClassName(), currentRequestId, data));
            this.updating = false;
        } catch (error) {
            this.lastUpdateSuccessful = false;
            this.lastData = null;
            this.lastDataTimestamp = null;
            this.lastError = error;
            this.updateKoSource.next(new UpdateKoEventPayload(this.getClassName(), currentRequestId, error));
            this.updating = false;
            switch (error.type) {
                case KpiServiceErrorType.noData: break;
                case undefined:
                default: {
                    this.logger.error(error.error ? error.error.errorMessage : error.message);
                    break;
                }
            }
        }
    }

    public clearCache(): void {
        this.lastUpdateSuccessful = false;
    }

    public cacheExpired(): boolean {
        let result: boolean = true;
        if (this.lastUpdateSuccessful) {
            let now = moment();
            let duration = moment.duration(now.diff(moment(this.lastDataTimestamp)));
            if (duration.asMilliseconds() < this.cacheExpiration) result = false;
        }

        return result;
    }

    private async useCachedResponse(currentRequestId: number): Promise<void> {
        if (this.lastUpdateSuccessful) {
            this.updateOkSource.next(new UpdateOkEventPayload(this.getClassName(), currentRequestId, this.lastData));
            this.updating = false;
        } else {
            this.updateKoSource.next(new UpdateKoEventPayload(this.getClassName(), currentRequestId, this.lastError));
            this.updating = false;
        }
    }

    private readonly updatingSource: Subject<UpdatingEventPayLoad> = new Subject<UpdatingEventPayLoad>();
    public get Updating(): Observable<UpdatingEventPayLoad> {
        return this.updatingSource.asObservable();
    }

    private readonly updateOkSource: Subject<UpdateOkEventPayload<T>> = new Subject<UpdateOkEventPayload<T>>();
    public get UpdateOk(): Observable<UpdateOkEventPayload<T>> {
        return this.updateOkSource.asObservable();
    }

    private readonly updateKoSource: Subject<UpdateKoEventPayload> = new Subject<UpdateKoEventPayload>();
    public get UpdateKo(): Observable<UpdateKoEventPayload> {
        return this.updateKoSource.asObservable();
    }
}
